import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, Upload, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const SAMPLE = `full_name,phone,email,parent_phone,parent_email,parent_name
Alexandre Binet,8195551111,alex@email.com,,,
Cataleya Jean-Pierre,,,+18195278489,,Ralph D. Jean-Pierre`;

export default function ContactsBulkModule() {
  const [coverage, setCoverage] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const loadCoverage = async () => {
    try {
      const { data } = await api.get("/contacts/coverage");
      setCoverage(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadCoverage(); }, []);

  const handleImport = async () => {
    if (!csvText.trim()) {
      toast.error("Colle d'abord ton CSV dans le textarea");
      return;
    }
    if (!csvText.includes(",")) {
      toast.error("Format CSV attendu (séparateur virgule)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/contacts/bulk-update", {
        csv_text: csvText,
        fuzzy: true,
      });
      setResult(data);
      toast.success(data.summary);
      loadCoverage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur import");
    } finally {
      setLoading(false);
    }
  };

  const handleSMSBroadcast = async () => {
    try {
      const { data } = await api.post("/sms/carnet-broadcast/preview", { only_with_techs: true, lang: "fr" });
      const s = data.summary;
      const ok = window.confirm(
        `📱 SMS Broadcast — Aperçu\n\n` +
        `Total candidats : ${s.total_candidates}\n` +
        `Prêts (téléphone OK) : ${s.ready_to_send}\n` +
        `Sans téléphone : ${s.missing_phone}\n` +
        `Coût estimé Twilio : ~${s.estimated_cost_cad} CAD\n\n` +
        `Envoyer le SMS à ${s.ready_to_send} élèves maintenant ?`
      );
      if (!ok) return;
      const send = await api.post("/sms/carnet-broadcast/send?confirm=true", { only_with_techs: true, lang: "fr" });
      const ss = send.data.summary;
      toast.success(`✅ ${ss.sent} SMS envoyés · ${ss.errors} erreurs · ${ss.skipped_idempotent} ignorés`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur SMS");
    }
  };

  const handleContactUpdateBroadcast = async () => {
    const ok = window.confirm(
      `📞 Envoyer un email "Mets à jour tes coordonnées" aux élèves qui ont 1 email mais manquent phone/adresse ?\n\n` +
      `Lien personnel signé (90j) · Form mobile en 30s · Auto-merge dans la DB.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/email/contact-update-broadcast/send?confirm=true");
      const s = data.summary;
      toast.success(`✅ ${s.drafts_created} brouillons créés · ${s.errors} erreurs · ${s.skipped} déjà envoyés`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleEmailBroadcast = async () => {
    try {
      const { data } = await api.post("/email/carnet-broadcast/preview", { only_with_techs: true });
      const s = data.summary;
      const ok = window.confirm(
        `✉️ Email Broadcast — Aperçu (Gmail drafts)\n\n` +
        `Prêts (email OK) : ${s.ready_to_send}\n` +
        `Sans email : ${s.missing_email}\n` +
        `Coût : 0$ (Gmail gratuit)\n\n` +
        `Créer ${s.ready_to_send} brouillons Gmail maintenant ?\n` +
        `Tu valideras chaque brouillon dans Gmail avant d'envoyer.`
      );
      if (!ok) return;
      const send = await api.post("/email/carnet-broadcast/send?confirm=true", { only_with_techs: true });
      const ss = send.data.summary;
      toast.success(`✅ ${ss.drafts_created} brouillons créés · ${ss.errors} erreurs · ${ss.skipped_idempotent} déjà créés`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Email broadcast");
    }
  };

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="contacts-bulk-module">
      <PageHeader
        title="Import Contacts Élèves"
        icon={Users}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Import Contacts" }]}
        subtitle="Colle un CSV pour mettre à jour phones + emails de tes élèves. Match fuzzy par nom de famille."
      />

      {/* Coverage stats */}
      {coverage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="coverage-total">
            <div className="text-3xl font-bold text-zinc-100">{coverage.total}</div>
            <div className="text-xs text-zinc-500 uppercase">Élèves total</div>
          </div>
          <div className="bg-zinc-900 border border-emerald-900 rounded-lg p-4" data-testid="coverage-phone">
            <div className="text-3xl font-bold text-emerald-400 flex items-center gap-2">
              <Phone className="w-6 h-6" /> {coverage.with_phone}
            </div>
            <div className="text-xs text-zinc-500 uppercase">Avec téléphone</div>
          </div>
          <div className="bg-zinc-900 border border-blue-900 rounded-lg p-4" data-testid="coverage-email">
            <div className="text-3xl font-bold text-blue-400 flex items-center gap-2">
              <Mail className="w-6 h-6" /> {coverage.with_email}
            </div>
            <div className="text-xs text-zinc-500 uppercase">Avec email</div>
          </div>
          <div className="bg-zinc-900 border border-red-900 rounded-lg p-4" data-testid="coverage-missing">
            <div className="text-3xl font-bold text-red-400">{coverage.missing_phone}</div>
            <div className="text-xs text-zinc-500 uppercase">Manquent un phone</div>
          </div>
        </div>
      )}

      {/* CSV input */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-400" /> CSV à importer
          </h2>
          <button
            data-testid="load-sample-btn"
            onClick={() => setCsvText(SAMPLE)}
            className="text-xs text-amber-300 hover:text-amber-200 underline"
          >
            Charger un exemple
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          <strong>Header attendu</strong> : <code className="bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300">full_name,phone,email,parent_phone,parent_email,parent_name</code><br />
          Format phone : <code className="bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300">8195551234</code> ou <code className="bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300">+18195551234</code>
        </p>
        <textarea
          data-testid="csv-textarea"
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder={SAMPLE}
          rows={14}
          className="w-full bg-zinc-950 border border-zinc-700 rounded-lg p-3 text-sm font-mono text-zinc-200 placeholder-zinc-600 focus:border-amber-500 focus:outline-none"
        />
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            data-testid="import-btn"
            disabled={loading || !csvText.trim()}
            onClick={handleImport}
            className="bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-semibold px-5 py-2.5 rounded-lg"
          >
            {loading ? "Import en cours…" : "📥 Importer les contacts"}
          </button>
          {coverage?.with_phone > 0 && (
            <button
              data-testid="sms-broadcast-page-btn"
              onClick={handleSMSBroadcast}
              className="bg-emerald-700 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-lg flex items-center gap-2"
            >
              📱 Envoyer SMS QR à {coverage.with_phone} élève{coverage.with_phone > 1 ? "s" : ""}
            </button>
          )}
          {coverage?.with_email > 0 && (
            <button
              data-testid="email-broadcast-page-btn"
              onClick={handleEmailBroadcast}
              className="bg-blue-700 hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg flex items-center gap-2"
            >
              ✉️ Créer {coverage.with_email} brouillons Gmail QR
            </button>
          )}
          {coverage?.missing_phone > 0 && (
            <button
              data-testid="contact-update-broadcast-btn"
              onClick={handleContactUpdateBroadcast}
              className="bg-purple-700 hover:bg-purple-600 text-white px-5 py-2.5 rounded-lg flex items-center gap-2"
            >
              📞 Demander aux élèves de compléter leurs coordonnées
            </button>
          )}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4" data-testid="import-result">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Résultat : {result.summary}
          </h3>

          {result.updated?.length > 0 && (
            <div data-testid="updated-list">
              <div className="text-sm text-emerald-300 mb-2 font-medium">✅ {result.updated.length} mis à jour</div>
              <div className="bg-zinc-950 rounded-lg p-3 max-h-64 overflow-y-auto">
                {result.updated.map((u, i) => (
                  <div key={i} className="text-xs text-zinc-300 py-0.5">
                    {u.name} <span className="text-zinc-600">→ {u.fields.filter(f => f !== "updated_at").join(", ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.not_found?.length > 0 && (
            <div data-testid="not-found-list">
              <div className="text-sm text-amber-300 mb-2 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {result.not_found.length} non trouvés
              </div>
              <div className="bg-zinc-950 rounded-lg p-3 max-h-32 overflow-y-auto text-xs text-zinc-400">
                {result.not_found.join(" · ")}
              </div>
            </div>
          )}

          {result.ambiguous?.length > 0 && (
            <div data-testid="ambiguous-list">
              <div className="text-sm text-red-300 mb-2 font-medium">⚠️ {result.ambiguous.length} ambigus (plusieurs matches)</div>
              <div className="bg-zinc-950 rounded-lg p-3 max-h-32 overflow-y-auto text-xs text-zinc-400">
                {result.ambiguous.map((a, i) => (
                  <div key={i}>
                    <strong>{a.name}</strong> → {a.matches.join(", ")}
                  </div>
                ))}
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                Ajoute le prénom + nom complet dans ton CSV pour éviter l'ambiguïté.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
